import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/logo";
import { HomepageDisclaimer } from "@/components/disclaimer-banner";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { checkSignupRateLimit, recordSignupForAlerting } from "@/lib/viral-protection.functions";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Create account — ForexMind AI" },
      { name: "description", content: "Create your ForexMind AI account today and start receiving institutional-grade, AI-powered forex trading signals across the major pairs and gold." },
      { property: "og:title", content: "Create your ForexMind AI account" },
      { property: "og:description", content: "Join traders using AI-driven confluence signals across major forex pairs and gold." },
      { property: "og:url", content: "https://forexmind.ai/signup" },
    ],
    links: [
      { rel: "canonical", href: "https://forexmind.ai/signup" },
    ],
  }),
  component: SignupPage,
});

const PLANS = [
  { id: "free", name: "Free", price: "$0", desc: "3 pairs · delayed signals" },
  { id: "pro", name: "Pro", price: "$29", desc: "All 8 pairs · real-time" },
  { id: "elite", name: "Elite", price: "$79", desc: "Everything + API access" },
] as const;

function SignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [plan, setPlan] = useState<"free" | "pro" | "elite">("free");
  const [terms, setTerms] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void import("@/integrations/supabase/client").then(async ({ supabase }) => {
      const { data } = await supabase.auth.getSession();
      if (active && data.session) navigate({ to: "/dashboard", replace: true });
      const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
        if (session) navigate({ to: "/dashboard", replace: true });
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [navigate]);

  const signUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 6) return setErr("Password must be at least 6 characters");
    if (password !== confirm) return setErr("Passwords don't match");
    if (!terms) return setErr("Please accept the terms to continue");
    setLoading(true);

    // Viral-traffic guard: per-IP signup rate limit (5/hour). Fails open if the DB check errors.
    try {
      const rl = await checkSignupRateLimit();
      if (!rl.allowed) {
        setLoading(false);
        setErr("Too many signup attempts from your network. Please try again in an hour.");
        return;
      }
    } catch {
      // fail open
    }

    const { supabase } = await import("@/integrations/supabase/client");
    const origin = typeof window === "undefined" ? "" : window.location.origin;
    let referred_by_code: string | null = null;
    try {
      referred_by_code = typeof window !== "undefined" ? localStorage.getItem("forexmind_ref") : null;
    } catch {}
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${origin}/auth/callback`,
        data: { full_name: fullName, display_name: fullName, plan, referred_by_code },
      },
    });
    setLoading(false);
    if (error) {
      setErr(error.message);
      return;
    }
    // Fire-and-forget: increment daily signup counter and alert admin if threshold crossed.
    void recordSignupForAlerting().catch(() => undefined);
    setSentEmail(email);
    setEmailSent(true);
    toast.success("Account created — check your email to confirm.");
  };

  if (emailSent) {
    return (
      <div
        className="relative grid min-h-screen place-items-center overflow-hidden bg-grid px-4"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="absolute inset-0" style={{ background: "var(--gradient-glow)" }} />
        <div className="relative w-full max-w-md">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" variant="stacked" />
          </div>
          <Card className="border-border/60 bg-card/80 p-8 backdrop-blur shadow-[var(--shadow-card)] text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-3xl">
              📧
            </div>
            <h1 className="font-display text-2xl font-bold text-foreground">Check your email</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We sent a confirmation link to
            </p>
            <p className="mt-1 font-mono text-sm font-semibold text-primary">{sentEmail}</p>
            <p className="mt-4 text-xs text-muted-foreground">
              Click the link in your email to activate your ForexMind AI account.
              Check your spam folder if you don't see it within a minute.
            </p>
            <button
              type="button"
              onClick={() => {
                setEmailSent(false);
                setSentEmail("");
              }}
              className="mt-6 text-xs text-muted-foreground hover:text-foreground"
            >
              ← Use a different email
            </button>
            <div className="mt-6 border-t border-border/60 pt-4">
              <Link to="/login" className="text-sm font-semibold text-primary hover:underline">
                Back to sign in
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-grid"
      style={{ background: "var(--gradient-hero)" }}
    >
      <div className="absolute inset-0" style={{ background: "var(--gradient-glow)" }} />
      <div className="relative">
        <HomepageDisclaimer />
      </div>
      <div className="relative grid place-items-center px-4 py-10">
      <div className="relative w-full max-w-lg">
        <div className="mb-6 flex justify-center">
          <Logo size="lg" variant="stacked" />
        </div>
        <Card className="border-border/60 bg-card/80 p-7 backdrop-blur shadow-[var(--shadow-card)]">
          <h1 className="font-display text-2xl font-bold text-foreground">Start your edge today</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Join traders using AI-driven confluence signals.
          </p>
          <form onSubmit={signUp} className="mt-6 space-y-4">
            <Field
              label="Full name"
              type="text"
              value={fullName}
              onChange={setFullName}
              placeholder="Jane Trader"
            />
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="trader@email.com"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="At least 6 chars"
              />
              <Field
                label="Confirm"
                type="password"
                value={confirm}
                onChange={setConfirm}
                placeholder="Repeat password"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Choose your plan
              </Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {PLANS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlan(p.id)}
                    className={cn(
                      "rounded-lg border p-3 text-left transition-colors",
                      plan === p.id
                        ? "border-primary bg-primary/10"
                        : "border-border/60 bg-card/40 hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-sm font-bold">{p.name}</span>
                      <span className="font-mono text-xs text-primary">{p.price}</span>
                    </div>
                    <p className="mt-1 text-[10px] leading-tight text-muted-foreground">{p.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-primary"
              />
              <span>
                I agree to the <span className="underline">Terms of Service</span> and{" "}
                <span className="underline">Privacy Policy</span>.
              </span>
            </label>

            {err && (
              <div className="rounded-md border border-sell/40 bg-sell/10 px-3 py-2 text-xs text-sell">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
            <div className="relative my-2 flex items-center gap-3">
              <div className="h-px flex-1 bg-border/60" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={async () => {
                const { lovable } = await import("@/integrations/lovable");
                const res = await lovable.auth.signInWithOAuth("google", {
                  redirect_uri: window.location.origin + "/auth/callback",
                });
                if (res.error) toast.error(res.error.message ?? "Google sign-in failed");
              }}
            >
              Continue with Google
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </Card>
      </div>
      </div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="bg-input/50 font-mono"
      />
    </div>
  );
}
